import { useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { useShops } from './useShops';
import { useEmployees } from './useEmployees';
import { useSchedules } from './useSchedules';
import { getWeekStart, getWeekDates, formatDate } from '../utils/scheduleHelpers';

export interface ChecklistItem {
  id: string;
  label: string;
  description: string;
  completed: boolean;
  optional?: boolean;
  route?: string;
  action?: 'openSettings' | 'openTeamManagement';
}

export interface SetupChecklistState {
  items: ChecklistItem[];
  completedCount: number;
  totalCount: number;
  progressPercent: number;
  allCompleted: boolean;
  isVisible: boolean;
  isDismissed: boolean;
  isLoading: boolean;
  dismiss: () => void;
}

export function useSetupChecklist(): SetupChecklistState {
  const { user, updateUserProfile } = useAuth();
  const { organization, members, loading: orgLoading } = useOrganization();
  const { shops, isLoading: shopsLoading } = useShops();
  const { employees, isLoading: employeesLoading } = useEmployees();

  // Calculate current week date range (same as useDashboard)
  const dateRange = useMemo(() => {
    const now = new Date();
    const weekStart = getWeekStart(now);
    const weekDates = getWeekDates(weekStart);
    const startDate = formatDate(weekDates[0]);
    const endDate = formatDate(weekDates[weekDates.length - 1]);
    return { startDate, endDate };
  }, []);

  // Only fetch schedules if shops exist (matches useDashboard behavior)
  const shouldFetchSchedules = !shopsLoading && shops.length > 0;
  const { schedules } = useSchedules(
    undefined,
    undefined,
    shouldFetchSchedules ? dateRange : undefined
  );

  const isLoading = shopsLoading || employeesLoading || orgLoading;
  const isDismissed = user?.setupChecklistDismissed === true;

  // Completion detection
  const hasShop = shops.length > 0;

  const hasDefaultHours = useMemo(() => {
    return shops.some((shop) => {
      const hours = shop.defaultHours;
      if (!hours) return false;
      return Object.values(hours).some((day) => day.isOpen);
    });
  }, [shops]);

  const hasCoverage = useMemo(() => {
    return shops.some((shop) => {
      const coverage = shop.defaultCoverage;
      if (!coverage) return false;
      return Object.values(coverage).some(
        (dayCoverage) => Array.isArray(dayCoverage) && dayCoverage.length > 0
      );
    });
  }, [shops]);

  const hasEmployees = employees.length > 0;

  const hasConfiguredSettings = organization?.settings?.settingsConfigured === true;

  const hasSchedule = useMemo(() => {
    return schedules.length > 0;
  }, [schedules]);

  const hasTeamMembers = members.length > 1;

  const items: ChecklistItem[] = useMemo(
    () => [
      {
        id: 'add-shop',
        label: 'Add your first shop',
        description: 'Create a shop location to start scheduling',
        completed: hasShop,
        route: '/shops/information',
      },
      {
        id: 'set-hours',
        label: 'Set default hours',
        description: 'Define when your shop is open each day',
        completed: hasDefaultHours,
        route: '/shops/information',
      },
      {
        id: 'set-coverage',
        label: 'Set coverage requirements',
        description: 'Specify how many staff you need per shift',
        completed: hasCoverage,
        route: '/shops/coverage',
      },
      {
        id: 'add-employees',
        label: 'Add employees',
        description: 'Add your team members to start scheduling',
        completed: hasEmployees,
        route: '/employees/information',
      },
      {
        id: 'configure-settings',
        label: 'Configure settings',
        description: 'Customize availability mode and permissions',
        completed: hasConfiguredSettings,
        action: 'openSettings',
      },
      {
        id: 'create-schedule',
        label: 'Create first schedule',
        description: 'Build your first weekly schedule',
        completed: hasSchedule,
        route: '/schedules/employees',
      },
      {
        id: 'invite-team',
        label: 'Invite team members',
        description: 'Invite managers or admins to help manage',
        completed: hasTeamMembers,
        optional: true,
        action: 'openTeamManagement',
      },
    ],
    [hasShop, hasDefaultHours, hasCoverage, hasEmployees, hasConfiguredSettings, hasSchedule, hasTeamMembers]
  );

  // Only count required items toward progress
  const requiredItems = items.filter((item) => !item.optional);
  const completedCount = requiredItems.filter((item) => item.completed).length;
  const totalCount = requiredItems.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allCompleted = completedCount === totalCount;

  const isOwner = user?.role === 'owner';
  const isVisible = isOwner && !isDismissed && !allCompleted && !isLoading;

  const dismiss = useCallback(() => {
    updateUserProfile({ setupChecklistDismissed: true });
  }, [updateUserProfile]);

  return {
    items,
    completedCount,
    totalCount,
    progressPercent,
    allCompleted,
    isVisible,
    isDismissed,
    isLoading,
    dismiss,
  };
}
